import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import SpanishDatePicker from "@/components/SpanishDatePicker";
import {
  Shield, Users, Trophy, Activity, AlertTriangle, Target,
  TrendingUp, Briefcase, Calendar, UserX, ArrowRight, Repeat,
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const fmtPct = (n) => (n == null ? "—" : `${n}%`);
const seasonStartIso = () => {
  // From June onwards we already plan for the NEW season (e.g. 26/27).
  const today = new Date();
  const year = today.getMonth() >= 5 ? today.getFullYear() : today.getFullYear() - 1;
  return `${year}-07-01`;
};
const seasonLabel = () => {
  const y = Number(seasonStartIso().slice(0, 4));
  return `${y}/${String(y + 1).slice(-2)}`;
};

export default function CoordinatorKPIs() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState(seasonStartIso());
  const [dateTo, setDateTo] = useState("");

  useEffect(() => {
    if (!authLoading && user && user.role !== "admin") {
      toast.error("Esta sección es solo para el Director Deportivo.");
      navigate("/dashboard", { replace: true });
    }
  }, [authLoading, user, navigate]);

  const fetchData = (df, dt) => {
    setLoading(true);
    const params = new URLSearchParams();
    if (df) params.set("date_from", df);
    if (dt) params.set("date_to", dt);
    api.get(`/coordinator/kpis${params.toString() ? `?${params}` : ""}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(dateFrom, dateTo); /* eslint-disable-next-line */ }, []);

  const g = data?.globals;
  const perTeam = data?.per_team || [];
  const alerts = data?.alerts || [];
  const topScorers = data?.top_scorers || [];
  const topAttendance = data?.top_attendance || [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-black tracking-tight text-slate-900 flex items-center gap-2"
              style={{ fontFamily: "Nunito" }}>
            <Briefcase className="h-7 w-7 text-purple-600" /> Cuadro de Mando del Coordinador
          </h1>
          <p className="text-sm text-slate-500">
            Vista global del club · KPIs agregados, ranking de equipos y alertas de riesgo · Temporada {seasonLabel()}.
          </p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <Button
            variant="outline"
            className="border-purple-300 text-purple-700 hover:bg-purple-50"
            onClick={() => navigate("/migracion")}
            data-testid="coord-migration-link"
          >
            <Repeat className="h-4 w-4 mr-1.5" /> Migración {seasonLabel()}
          </Button>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500">Desde</Label>
            <SpanishDatePicker value={dateFrom} onChange={setDateFrom}
                               className="w-40" data-testid="coord-date-from" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500">Hasta</Label>
            <SpanishDatePicker value={dateTo} onChange={setDateTo}
                               className="w-40" data-testid="coord-date-to" />
          </div>
          <Button
            onClick={() => fetchData(dateFrom, dateTo)}
            disabled={loading}
            className="bg-purple-600 hover:bg-purple-700 text-white"
            data-testid="coord-refresh"
          >
            <Calendar className="h-4 w-4 mr-1.5" /> {loading ? "Cargando…" : "Aplicar"}
          </Button>
        </div>
      </div>

      {/* Global KPI Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        <KPI icon={Shield} label="Equipos" value={g?.teams ?? "—"} tone="purple" testId="kpi-teams" />
        <KPI icon={Users} label="Jugadores" value={g?.players ?? "—"} tone="blue" testId="kpi-players" />
        <KPI icon={Activity} label="Entrenos" value={g?.trainings ?? "—"} tone="slate" testId="kpi-trainings" />
        <KPI icon={TrendingUp} label="% Asistencia" value={fmtPct(g?.attendance_pct)} tone="emerald" testId="kpi-attendance" />
        <KPI icon={Trophy} label="Partidos" value={`${g?.matches_played ?? 0}`}
             sub={g ? `V ${g.wins} · E ${g.draws} · D ${g.losses}` : ""} tone="amber" testId="kpi-matches" />
        <KPI icon={Target} label="% Victorias" value={fmtPct(g?.win_pct)} tone="emerald" testId="kpi-win-pct" />
        <KPI icon={AlertTriangle} label="Alertas" value={g?.alerts_count ?? "—"} tone="rose" testId="kpi-alerts" />
      </div>

      {/* Per-team table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Shield className="h-4 w-4 text-purple-600" /> Ranking de equipos
          </CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
              <tr>
                <th className="px-2 py-2 text-left font-bold">#</th>
                <th className="px-2 py-2 text-left font-bold">Equipo</th>
                <th className="px-2 py-2 text-left font-bold">Categoría</th>
                <th className="px-2 py-2 text-right font-bold">Jug.</th>
                <th className="px-2 py-2 text-right font-bold">PJ</th>
                <th className="px-2 py-2 text-right font-bold text-emerald-700">V</th>
                <th className="px-2 py-2 text-right font-bold text-amber-700">E</th>
                <th className="px-2 py-2 text-right font-bold text-rose-700">D</th>
                <th className="px-2 py-2 text-right font-bold">GF</th>
                <th className="px-2 py-2 text-right font-bold">GC</th>
                <th className="px-2 py-2 text-right font-bold">Dif</th>
                <th className="px-2 py-2 text-right font-bold">Pts</th>
                <th className="px-2 py-2 text-right font-bold">% Asis.</th>
              </tr>
            </thead>
            <tbody data-testid="coord-team-table">
              {perTeam.length === 0 && (
                <tr><td colSpan={13} className="py-8 text-center text-slate-400 italic">Sin datos.</td></tr>
              )}
              {perTeam.map((t, i) => (
                <tr key={t.team_id} className="border-b border-slate-100 hover:bg-slate-50/40">
                  <td className="px-2 py-2 text-slate-400 font-bold">{i + 1}</td>
                  <td className="px-2 py-2 font-semibold text-slate-800">
                    <Link to={`/teams/${t.team_id}/fisico`} className="hover:text-purple-700">{t.name}</Link>
                  </td>
                  <td className="px-2 py-2 text-xs text-slate-500">{t.category}</td>
                  <td className="px-2 py-2 text-right">{t.players}</td>
                  <td className="px-2 py-2 text-right">{t.pj}</td>
                  <td className="px-2 py-2 text-right text-emerald-700 font-bold">{t.v}</td>
                  <td className="px-2 py-2 text-right text-amber-700 font-bold">{t.e}</td>
                  <td className="px-2 py-2 text-right text-rose-700 font-bold">{t.d}</td>
                  <td className="px-2 py-2 text-right">{t.gf}</td>
                  <td className="px-2 py-2 text-right">{t.gc}</td>
                  <td className={`px-2 py-2 text-right font-bold ${t.diff > 0 ? "text-emerald-700" : t.diff < 0 ? "text-rose-700" : "text-slate-600"}`}>
                    {t.diff > 0 ? "+" : ""}{t.diff}
                  </td>
                  <td className="px-2 py-2 text-right text-purple-700 font-black">{t.points}</td>
                  <td className="px-2 py-2 text-right">
                    <Link to={`/teams/${t.team_id}/detalle-stats?metric=attendance&scope=no_pre`} className="font-bold text-purple-700 hover:underline" data-testid={`coord-team-att-${t.team_id}`}>{fmtPct(t.attendance_pct)}</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Risk alerts */}
      <Card className={alerts.length > 0 ? "border-rose-200" : ""}>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <AlertTriangle className={`h-4 w-4 ${alerts.length > 0 ? "text-rose-600" : "text-slate-400"}`} />
            Alertas de riesgo
          </CardTitle>
          <div className="text-xs text-slate-500">
            ACWR &gt; 1.5 (carga aguda alta) · Wellness 2 días en rojo
          </div>
        </CardHeader>
        <CardContent>
          {alerts.length === 0 ? (
            <div className="text-sm text-slate-400 italic py-6 text-center">
              Ningún jugador en zona de riesgo en este momento.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="coord-alerts">
              {alerts.map((a) => (
                <li key={`${a.player_id}-${a.type}`} className="py-2 flex items-center justify-between gap-3">
                  <div>
                    <div className="font-semibold text-slate-800">{a.player_name}</div>
                    <div className="text-xs text-slate-500">{a.team_name}</div>
                  </div>
                  <div className={`text-xs font-bold px-2 py-1 rounded border ${
                    a.type === "acwr"
                      ? "bg-rose-50 text-rose-700 border-rose-300"
                      : "bg-amber-50 text-amber-700 border-amber-300"
                  }`}>
                    {a.label}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Top lists */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <TopList
          title="Top goleadores"
          icon={Target}
          items={topScorers}
          renderName={(i) => i.name}
          renderTeam={(i) => i.team_name}
          renderValue={(i) => `${i.goals} gol${i.goals === 1 ? "" : "es"}`}
          emptyLabel="Aún no hay goles registrados en el periodo."
          testId="coord-top-scorers"
        />
        <TopList
          title="Top % asistencia (entrenos)"
          icon={TrendingUp}
          items={topAttendance}
          renderName={(i) => i.player_name}
          renderTeam={(i) => i.team_name}
          renderValue={(i) => `${i.attendance_pct}% (${i.asiste}/${i.total_trainings})`}
          emptyLabel="Sin datos suficientes de asistencia."
          testId="coord-top-attendance"
          onItem={(i) => navigate(`/players/${i.player_id}/detalle-asistencia?scope=no_pre`)}
        />
      </div>

      <StaffAttendance dateFrom={dateFrom} dateTo={dateTo} />

      <CategoryMismatches />
    </div>
  );
}


function StaffAttendance({ dateFrom, dateTo }) {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams();
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    api.get(`/coordinator/staff-attendance${params.toString() ? `?${params}` : ""}`)
      .then((r) => setRows(r.data.rows || []))
      .catch((e) => toast.error(formatApiError(e)));
  }, [dateFrom, dateTo]);

  return (
    <Card className="border-slate-200" data-testid="staff-attendance-summary-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Users className="h-4 w-4 text-purple-600" /> Asistencia del cuerpo técnico
        </CardTitle>
        <p className="text-xs text-slate-500 mt-1">
          % de asistencia de cada miembro del staff a los entrenamientos de su equipo
          (sobre los entrenos con asistencia de staff registrada).
        </p>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        {rows === null ? (
          <div className="text-sm text-slate-400 italic py-4 text-center">Cargando…</div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-slate-400 italic py-4 text-center">
            No hay staff asignado a los equipos todavía.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
              <tr>
                <th className="px-2 py-2 text-left font-bold">Equipo</th>
                <th className="px-2 py-2 text-left font-bold">Rol</th>
                <th className="px-2 py-2 text-left font-bold">Nombre</th>
                <th className="px-2 py-2 text-right font-bold">Entrenos</th>
                <th className="px-2 py-2 text-right font-bold">Registrados</th>
                <th className="px-2 py-2 text-right font-bold">Asiste</th>
                <th className="px-2 py-2 text-right font-bold">% Asis.</th>
              </tr>
            </thead>
            <tbody data-testid="staff-attendance-table">
              {rows.map((r) => (
                <tr key={`${r.team_id}-${r.user_id}-${r.role}`} className="border-b border-slate-100 hover:bg-slate-50/40">
                  <td className="px-2 py-2 font-semibold text-slate-800">{r.team_name}</td>
                  <td className="px-2 py-2 text-xs text-slate-500">{r.role}</td>
                  <td className="px-2 py-2">{r.name}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{r.trainings_total}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{r.registered}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{r.attended}</td>
                  <td className="px-2 py-2 text-right">
                    {r.attendance_pct == null ? (
                      <span className="text-slate-400 italic text-xs">Sin registro</span>
                    ) : (
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-bold ${
                        r.attendance_pct >= 80
                          ? "bg-emerald-100 text-emerald-800"
                          : r.attendance_pct >= 50
                            ? "bg-amber-100 text-amber-700"
                            : "bg-red-100 text-red-700"
                      }`}>
                        {r.attendance_pct}%
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}


function CategoryMismatches() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/coordinator/category-mismatches")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Card className="border-slate-200">
        <CardContent className="p-6 text-slate-400 italic text-sm">Cargando control de cuadros…</CardContent>
      </Card>
    );
  }

  const rows = data?.mismatches || [];
  const total = data?.total_players_checked || 0;
  const season = data?.season_start_year;

  return (
    <Card className="border-slate-200" data-testid="control-cuadros-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <UserX className="h-4 w-4 text-purple-600" /> Control de cuadros
          <span className="text-xs font-normal text-slate-500 ml-1">
            (temporada {season}/{String((season || 0) + 1).slice(-2)})
          </span>
        </CardTitle>
        <p className="text-xs text-slate-500 mt-1">
          Jugadores cuya categoría del equipo no coincide con la sugerida por su año de nacimiento.
          Alevín F-11 se considera compatible con jugadores en edad Alevín.
        </p>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <div className="text-sm text-slate-500 italic py-6 text-center">
            {total === 0
              ? "Aún no hay jugadores con año de nacimiento registrado."
              : `Todo cuadra ✓ — los ${total} jugadores con año de nacimiento están en la categoría correcta.`}
          </div>
        ) : (
          <>
            <div className="text-xs text-slate-500 mb-3">
              <b>{rows.length}</b> jugador{rows.length === 1 ? "" : "es"} en categoría distinta a la sugerida
              (de {total} jugadores con año de nacimiento).
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="control-cuadros-table">
                <thead className="text-xs uppercase tracking-widest text-slate-500 border-b border-slate-100">
                  <tr>
                    <th className="text-left py-2">Jugador</th>
                    <th className="text-left py-2">Equipo actual</th>
                    <th className="text-left py-2 whitespace-nowrap">Año / edad</th>
                    <th className="text-left py-2">Categoría actual</th>
                    <th className="text-left py-2">Sugerida</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.player_id} className="border-b border-slate-50 hover:bg-slate-50">
                      <td className="py-2 font-semibold text-slate-800">{r.player_name}</td>
                      <td className="py-2 text-slate-600">{r.team_name}</td>
                      <td className="py-2 text-slate-600 tabular-nums whitespace-nowrap">
                        {r.birth_year} <span className="text-slate-400">({r.age} años)</span>
                      </td>
                      <td className="py-2">
                        <span className="inline-block bg-rose-100 text-rose-800 rounded px-2 py-0.5 text-xs font-semibold">
                          {r.current_category || "—"}
                        </span>
                      </td>
                      <td className="py-2">
                        <span className="inline-flex items-center gap-1">
                          <ArrowRight className="h-3 w-3 text-slate-400" />
                          <span className="inline-block bg-emerald-100 text-emerald-800 rounded px-2 py-0.5 text-xs font-semibold">
                            {r.suggested_category}
                          </span>
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function KPI({ icon: Icon, label, value, sub, tone = "slate", testId }) {
  const tones = {
    purple:  "bg-purple-50 border-purple-200 text-purple-800",
    blue:    "bg-blue-50 border-blue-200 text-blue-800",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    amber:   "bg-amber-50 border-amber-200 text-amber-800",
    rose:    "bg-rose-50 border-rose-200 text-rose-800",
    slate:   "bg-slate-50 border-slate-200 text-slate-800",
  };
  return (
    <div className={`border rounded-lg p-3 ${tones[tone] || tones.slate}`} data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest font-bold opacity-70">{label}</div>
        {Icon && <Icon className="h-4 w-4 opacity-60" />}
      </div>
      <div className="text-3xl font-black">{value}</div>
      {sub && <div className="text-[11px] opacity-70 mt-0.5">{sub}</div>}
    </div>
  );
}

function TopList({ title, icon: Icon, items, renderName, renderTeam, renderValue, emptyLabel, testId, onItem }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          {Icon && <Icon className="h-4 w-4 text-purple-600" />} {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <div className="text-sm text-slate-400 italic py-6 text-center">{emptyLabel}</div>
        ) : (
          <ol className="space-y-1.5" data-testid={testId}>
            {items.map((i, idx) => {
              const clickable = onItem && i.player_id;
              return (
              <li key={`${renderName(i)}-${idx}`}
                  className={`flex items-center gap-3 rounded-md px-1 ${clickable ? "cursor-pointer hover:bg-purple-50" : ""}`}
                  onClick={() => clickable && onItem(i)}
                  data-testid={clickable ? `${testId}-item-${idx}` : undefined}>
                <span className="w-6 h-6 rounded-full bg-purple-600 text-white text-xs font-bold flex items-center justify-center">
                  {idx + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-800 truncate">{renderName(i)}</div>
                  <div className="text-[11px] text-slate-500 truncate">{renderTeam(i)}</div>
                </div>
                <div className="text-sm font-bold text-purple-700 tabular-nums">{renderValue(i)}</div>
              </li>
              );
            })}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}
